import type { NavMenuItem, RailId } from './types';

/** Org-user menu items grouped by rail (IA). Icons attached in menuWithIcons. */
export const menuItemsByRail: Record<RailId, NavMenuItem[]> = {
  overview: [
    {
      labelKey: 'dashboard',
      path: '/dashboard',
      feature: 'overview',
    },
  ],
  patients: [
    {
      labelKey: 'patients',
      path: '/patients',
      feature: 'patients',
      permissions: ['patients:read'],
    },
    {
      labelKey: 'treatmentPlans',
      path: '/treatment-plans',
      feature: 'catalog',
      permissions: ['treatment-plan:read'],
    },
    {
      labelKey: 'treatmentSessions',
      path: '/treatment-sessions',
      feature: 'catalog',
      permissions: ['treatment-session:read'],
    },
    {
      labelKey: 'prescriptions',
      path: '/prescriptions',
      feature: 'catalog',
      permissions: ['prescriptions:read'],
    },
    {
      labelKey: 'labOrders',
      path: '/labo/orders',
      feature: 'labo',
      permissions: ['labo:read'],
    },
    {
      labelKey: 'payments',
      path: '/payments',
      feature: 'payments',
      permissions: ['payment:read'],
    },
  ],
  appointments: [
    {
      labelKey: 'appointments',
      path: '/appointments?view=calendar',
      feature: 'appointments',
      permissions: ['appointments:read'],
    },
  ],
  leads: [
    {
      labelKey: 'leads',
      path: '/leads',
      feature: 'leads',
      permissions: ['leads:read'],
    },
  ],
  marketing: [
    {
      labelKey: 'referrers',
      path: '/marketing/referrers',
      feature: 'reports',
      permissions: ['referrers:read', 'reports:read'],
    },
    {
      labelKey: 'referralCommissions',
      path: '/marketing/referral-commissions',
      feature: 'reports',
      permissions: ['referral_commission:read'],
    },
    {
      labelKey: 'promotions',
      path: '/marketing/promotions',
      feature: 'promotions',
      permissions: ['promotion:read'],
    },
    {
      labelKey: 'outbound',
      path: '/outbound',
      feature: 'messaging',
      permissions: ['users:manage'],
    },
  ],
  messaging: [
    {
      labelKey: 'messaging',
      path: '/messaging',
      match: 'exact',
      feature: 'messaging',
      permissions: ['messaging:read'],
    },
  ],
  internalChat: [
    {
      labelKey: 'internalChat',
      path: '/internal-chat',
      match: 'exact',
      feature: 'internal_chat',
      permissions: ['internal_chat:use'],
    },
  ],
  finance: [
    {
      labelKey: 'incomes',
      path: '/incomes',
      feature: 'finance',
      permissions: ['income:read'],
    },
    {
      labelKey: 'expenses',
      path: '/expenses',
      feature: 'finance',
      permissions: ['expense:read'],
    },
    {
      labelKey: 'cashbook',
      path: '/cashbook',
      feature: 'finance',
      permissions: ['finance:read'],
    },
  ],
  warehouse: [
    {
      labelKey: 'stockIn',
      path: '/inventory/stock-in',
      feature: 'warehouse',
      permissions: ['inventory:read'],
    },
    {
      labelKey: 'stockOut',
      path: '/inventory/stock-out',
      feature: 'warehouse',
      permissions: ['inventory:read'],
    },
    {
      labelKey: 'transfers',
      path: '/inventory/transfers',
      feature: 'warehouse',
      permissions: ['inventory:read'],
    },
    {
      labelKey: 'stocktakes',
      path: '/inventory/stocktakes',
      feature: 'warehouse',
      permissions: ['inventory:read'],
    },
    {
      labelKey: 'inventoryReports',
      path: '/inventory/report',
      feature: 'warehouse',
      permissions: ['inventory:read'],
    },
    {
      labelKey: 'warehouseConfig',
      path: '/inventory',
      match: 'catalog',
      feature: 'warehouse',
      permissions: ['inventory:read', 'suppliers:read'],
    },
  ],
  catalog: [
    {
      labelKey: 'treatmentCatalog',
      path: '/treatment-catalog',
      feature: 'catalog',
      permissions: ['treatment-catalog:read'],
    },
    {
      labelKey: 'treatmentUoms',
      path: '/treatment-uoms',
      feature: 'catalog',
      permissions: ['treatment-catalog:read'],
    },
    {
      labelKey: 'treatmentJournals',
      path: '/treatment-journals',
      feature: 'catalog',
      permissions: ['treatment-journal:read'],
    },
    {
      labelKey: 'drugs',
      path: '/drugs',
      feature: 'catalog',
      permissions: ['prescriptions:read', 'drugs:manage'],
    },
    {
      labelKey: 'prescriptionTemplates',
      path: '/prescription-templates',
      feature: 'catalog',
      permissions: ['prescriptions:read'],
    },
    {
      labelKey: 'appointmentTypes',
      path: '/appointment-types',
      feature: 'appointments',
      permissions: ['appointment-types:read'],
    },
    {
      labelKey: 'customerSources',
      path: '/customer-sources',
      feature: 'patients',
      permissions: ['customer-sources:read'],
    },
    {
      labelKey: 'diagnosisCodes',
      path: '/diagnosis-codes',
      feature: 'catalog',
      permissions: ['diagnosis-codes:read'],
    },
    {
      labelKey: 'labo',
      path: '/labo/master',
      match: 'laboMaster',
      feature: 'labo',
      permissions: ['labo:read'],
    },
    {
      labelKey: 'incomeCategories',
      path: '/income-categories',
      feature: 'finance',
      permissions: ['income:read'],
    },
    {
      labelKey: 'expenseCategories',
      path: '/expense-categories',
      feature: 'finance',
      permissions: ['expense:read'],
    },
    {
      labelKey: 'paymentMethods',
      path: '/payment-methods',
      feature: 'payments',
      permissions: ['payment-methods:read'],
    },
  ],
  work: [
    {
      labelKey: 'projects',
      path: '/projects',
      permissions: ['project:read'],
    },
    {
      labelKey: 'tasks',
      path: '/tasks?view=list&mine=0',
      permissions: ['task:read'],
    },
  ],
  hr: [
    {
      labelKey: 'staff',
      path: '/staff',
      permissions: ['staff:read'],
    },
    {
      labelKey: 'teamSchedule',
      path: '/scheduling',
      match: 'schedulingHub',
      feature: 'hr',
      permissions: [
        'scheduling:read',
        'scheduling:assignments:manage',
        'scheduling:shift-types:manage',
        'scheduling:templates:manage',
      ],
    },
    {
      labelKey: 'attendanceHistory',
      path: '/attendance',
      match: 'attendanceList',
      feature: 'hr',
      permissions: [
        'attendance:read',
        'attendance:request',
        'attendance:approve',
        'attendance:manage',
      ],
    },
    // Tăng ca chưa dùng — ẩn menu. Route /overtime vẫn giữ để bật lại.
    {
      labelKey: 'timeOff',
      path: '/scheduling/time-off',
      feature: 'hr',
      permissions: ['leave:read', 'leave:request'],
    },
    {
      labelKey: 'kpiSheets',
      path: '/kpi/cycles',
      feature: 'hr',
    },
    {
      labelKey: 'commissions',
      path: '/commissions',
      feature: 'hr',
      permissions: ['commission:read'],
    },
    {
      labelKey: 'payroll',
      path: '/payroll',
      match: 'payrollList',
      feature: 'hr',
      permissions: ['payroll:read'],
    },
    {
      labelKey: 'departments',
      path: '/departments',
      permissions: ['departments:read'],
    },
  ],
  reports: [
    {
      labelKey: 'reportRevenue',
      path: '/reports/revenue',
      feature: 'reports',
      permissions: ['reports:read'],
    },
    {
      labelKey: 'reportPatients',
      path: '/reports/patients',
      feature: 'reports',
      permissions: ['reports:read'],
    },
    {
      labelKey: 'reportReferrers',
      path: '/reports/referrers',
      feature: 'reports',
      permissions: ['reports:read'],
    },
    {
      labelKey: 'reportAppointments',
      path: '/reports/appointments',
      feature: 'reports',
      permissions: ['reports:read'],
    },
    {
      labelKey: 'reportTreatments',
      path: '/reports/treatments',
      feature: 'reports',
      permissions: ['reports:read'],
    },
    {
      labelKey: 'leadReports',
      path: '/reports/leads',
      feature: 'reports',
      permissions: ['leads:read'],
    },
    {
      labelKey: 'workTimesheet',
      path: '/work-timesheet',
      feature: 'hr',
      permissions: ['work-timesheet:read', 'work-timesheet:manage'],
    },
  ],
  settings: [
    {
      labelKey: 'users',
      path: '/users',
      permissions: ['users:manage'],
    },
    {
      labelKey: 'permissionGroups',
      path: '/permission-groups',
      permissions: ['users:manage'],
    },
    {
      labelKey: 'auditLog',
      path: '/audit',
      permissions: ['audit:read'],
    },
    {
      labelKey: 'automation',
      path: '/automation',
      feature: 'automation',
      permissions: ['automation:read'],
    },
    {
      labelKey: 'settings',
      path: '/settings',
      permissions: ['users:manage'],
    },
  ],
};

export const platformMenuItems: NavMenuItem[] = [
  {
    labelKey: 'organizations',
    path: '/platform',
    match: 'exact',
  },
];
